
import { Component, Vue, Watch } from 'vue-property-decorator'
import { CreateLeak } from '@/api/leaks'
import LeakMediaUpload from './components/LeakMediaUpload/index.vue'
import LocationChooseDialog from '@/components/LocationChooseDialog/index.vue'
import LocationAutoDetect, { LocationInfo as AutoLocationInfo } from '@/components/LocationAutoDetect/index.vue'
import { IAllUserInfo, FileType, ILeakRepair, IPipeDiameter, IPipeMaterial, IRoadSurface, IPoint, IProject, ILeakFile } from '@/api/types'
import { ListPipeDiameters, ListPipeMaterials, ListRoadSurfaces } from '@/api/baseData'
import { UserModule } from '@/store/modules/user'
import { IsMobile } from '@/utils/check'
import { goBack } from '@/router'

// 定义位置信息接口
interface LocationInfo {
  position: {
    longitude: number
    latitude: number
  }
  address: string
}

@Component({
  name: 'CreateLeakPage',
  components: {
    LeakMediaUpload,
    LocationChooseDialog,
    LocationAutoDetect
  }
})
export default class extends Vue {
  private isUploading = false
  private uploadProgress = 0
  private uploadingFiles = 0
  private mapDialogVisible = false
  private initialMapPosition: [number, number] | null = null

  // 初始位置和地址，可从路由参数中获取
  private location: IPoint = { longitude: 0, latitude: 0 }
  private referenceAddr: string = ''
  private referenceLeakRepair: ILeakRepair | null = null
  private detectUsers: IAllUserInfo[] = []

  private get showLocationButtonByOrgConfig() {
    if (UserModule.currentOrg?.orgConfig) {
      const obj = JSON.parse(UserModule.currentOrg.orgConfig)
      return obj.location_button
    }
    return false
  }

  // 自动获取位置相关状态
  private autoDetectActive = false
  private locating = false

  // 自动生成编号开关状态
  private autoGenerateIndex = true

  // 自定义编号时的数字部分
  private customIndexNumber = ''

  private temp = {
    index: undefined as string | undefined,
    roadInfo: undefined as string | undefined,
    size: undefined as number | undefined,
    material: undefined as string | undefined,
    location: { longitude: 0, latitude: 0 },
    referenceAddr: '',
    detailAddr: '',
    checkUserId: undefined as number | undefined,
    ProjectId: undefined as number | undefined,
    images: [] as string[],
    videos: [] as string[],
    referenceImages: [] as string[]
  }
  private imageFileList: any[] = []
  private videoFileList: any[] = []
  private pipeDiameterOptions: IPipeDiameter[] = []
  private pipeMaterialOptions: IPipeMaterial[] = []
  private roadSurfaceOptions: IRoadSurface[] = []
  private rules = {
    roadInfo: [{ required: true, message: '请输入路面信息', trigger: 'blur' }],
    size: [{ required: true, message: '请选择管径大小', trigger: 'blur' }],
    material: [{ required: true, message: '请选择管材', trigger: 'blur' }],
    referenceAddr: [{ required: true, message: '请输入参考地址', trigger: 'blur' }],
    detailAddr: [{ required: true, message: '请输入详细地址', trigger: 'blur' }],
    checkUserId: [{ required: true, message: '请选择检测人员', trigger: 'change' }],
    ProjectId: [{ required: true, message: '请选择所属部门', trigger: 'change' }],
    location: [{ required: true, message: '请选择位置', trigger: 'change' }],
    index: [{
      validator: (rule: any, value: string, callback: any) => {
        if (this.autoGenerateIndex) {
          // 自动编号模式下不检查值
          callback()
        } else if (!value || value.trim() === '') {
          // 手动编号模式下验证
          callback(new Error('编号不能为空'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }]
  }

  get isMobile() {
    return IsMobile()
  }

  get only1DetectUser() :boolean {
    return this.detectUsers.length === 1
  }

  get detectProjects(): IProject[] {
    if (!this.detectUsers || this.detectUsers.length === 0){
      return []
    }
    if (!this.temp.checkUserId) {
      return []
    }
    for (const user of this.detectUsers) {
      if (user.user.id === this.temp.checkUserId) {
        return user.projects
      }
    }
    return []
  }

  get submitButtonText() {
    if (!this.isUploading) return '确认'
    return `正在上传(${this.uploadProgress}%)`
  }

  // 获取当前选中项目的前缀
  get projectPrefix(): string {
    if (!this.temp.ProjectId) return ''

    const selectedProject = this.detectProjects.find(p => p.id === this.temp.ProjectId)
    return selectedProject ? selectedProject.prefix : ''
  }

  created() {
    this.fetchOptions()
    this.fetchUsers()
    this.initFromRouteParams()

    // 如果没有初始位置，启动自动获取
    if (!this.initialMapPosition) {
      this.startAutoDetectLocation()
    }
  }

  /**
   * 从路由参数初始化数据
   */
  private initFromRouteParams() {
    // 从路由参数中获取初始数据
    const routeParams = this.$route.params

    // 获取位置信息
    if (routeParams.position) {
      try {
        const position = JSON.parse(routeParams.position as string)
        this.location = {
          longitude: position.longitude,
          latitude: position.latitude
        }
        this.temp.location = this.location
        this.initialMapPosition = [this.location.longitude, this.location.latitude]
      } catch (error) {
        console.error('解析位置信息失败', error)
      }
    }

    // 获取地址信息
    if (routeParams.address) {
      this.referenceAddr = routeParams.address as string
      this.temp.referenceAddr = this.referenceAddr
      this.temp.detailAddr = this.referenceAddr
    }

    // 获取检测人员列表
    if (routeParams.detectUsers) {
      try {
        this.detectUsers = JSON.parse(routeParams.detectUsers as string)
        // 如果参数格式正确，设置默认检测人员
        if (this.detectUsers.length > 0) {
          this.temp.checkUserId = this.detectUsers[0].user.id
        }
      } catch (error) {
        console.error('解析检测人员参数失败', error)
      }
    }
  }

  /**
   * 获取检测人员数据
   */
  private async fetchUsers() {
    // 如果路由参数已经提供了检测人员列表，则无需再次获取
    if (this.detectUsers && this.detectUsers.length > 0) {
      return
    }

    try {
      // 这里应该调用API获取检测人员列表
      // 示例代码，实际实现需要根据API调整
      // const response = await GetDetectUsers()
      // this.detectUsers = response.data || []
      if (UserModule.allInfo) {
        this.detectUsers = [UserModule.allInfo]
      } else {
        this.detectUsers = []
      }

      // 设置默认检测人员
      if (this.detectUsers.length > 0) {
        this.temp.checkUserId = UserModule.userInfo?.id
      }
    } catch (error) {
      console.error('获取检测人员失败', error)
    }
  }

  @Watch('referenceLeakPoint', { deep: true })
  onReferenceChange(val: any) {
    if (val && Object.keys(val).length > 0) {
      // 如果有参考漏点，设置默认值
      if (val.detection) {
        this.temp.roadInfo = val.detection.roadInfo || ''
        this.temp.size = val.detection.size || 0
        this.temp.material = val.detection.material || ''
      }
    }
  }

  @Watch('temp.checkUserId')
  onCheckUserChange(val: number | undefined) {
    if (val) {
      // 当检测人员改变时，自动设置部门
      const projects = this.detectProjects
      if (projects.length > 0) {
        // 如果有可用部门，自动选择第一个
        this.temp.ProjectId = projects[0].id
      } else {
        // 如果没有可用部门，清空选择
        this.temp.ProjectId = undefined
      }
    } else {
      // 如果检测人员被清空，也清空部门选择
      this.temp.ProjectId = undefined
    }
  }

  @Watch('temp.ProjectId')
  async onCheckDeptChange(val: number | undefined) {
    if (val && !this.autoGenerateIndex) {
      // 手动模式下清空编号数字部分，让用户自己输入
      this.customIndexNumber = ''
    }
    // 如果项目改变了，重新验证表单
    this.$nextTick(() => {
      (this.$refs.createForm as any)?.validateField('index')
    })
  }

  @Watch('autoGenerateIndex')
  async onAutoGenerateChange(val: boolean) {
    if (!val) {
      // 切换到手动模式时，清空编号让用户输入
      this.customIndexNumber = ''
      this.temp.index = ''
    } else {
      // 切换到自动模式时，清空编号并临时添加一个值通过验证
      this.customIndexNumber = ''
      this.temp.index = '自动编号'
      // 清除表单验证错误
      this.$nextTick(() => {
        (this.$refs.createForm as any)?.clearValidate('index')
      })
    }
  }

  // 监听自定义数字的变化
  @Watch('customIndexNumber')
  onCustomIndexNumberChange(val: string) {
    if (!this.autoGenerateIndex && this.projectPrefix) {
      // 如果是手动模式且有项目前缀，设置完整index
      this.temp.index = val ? `${this.projectPrefix}-${val}` : ''

      // 验证表单
      this.$nextTick(() => {
        (this.$refs.createForm as any)?.validateField('index')
      })
    }
  }

  private async fetchOptions() {
    try {
      // 获取管径选项
      const diameterResponse = await ListPipeDiameters()
      this.pipeDiameterOptions = diameterResponse.data || []

      // 获取管材选项
      const materialResponse = await ListPipeMaterials()
      this.pipeMaterialOptions = materialResponse.data || []

      // 获取路面选项
      const roadResponse = await ListRoadSurfaces()
      this.roadSurfaceOptions = roadResponse.data || []

      // 设置默认选项
      this.setDefaultOptions()
    } catch (error) {
      console.error('获取选项数据失败', error)
    }
  }

  /**
   * 设置所有下拉框的默认选项
   */
  private setDefaultOptions() {
    this.temp.roadInfo = undefined
    this.temp.size = undefined
    this.temp.material = undefined

    // 设置检测人员默认值
    if (this.detectUsers.length > 0) {
      this.temp.checkUserId = this.detectUsers[0].user.id
    }
  }

  /**
   * 打开地图选择对话框
   */
  private openMapDialog() {
    // 如果已有位置，设置为初始位置
    if (this.temp.location.longitude !== 0 && this.temp.location.latitude !== 0) {
      this.initialMapPosition = [this.temp.location.longitude, this.temp.location.latitude]
    }
    this.mapDialogVisible = true
  }

  /**
   * 启动自动获取位置
   */
  private startAutoDetectLocation() {
    // 只有当没有初始位置时才自动获取
    if (!this.initialMapPosition) {
      this.autoDetectActive = true
    }
  }

  /**
   * 处理自动定位状态变化
   */
  private handleAutoLocating(status: boolean) {
    this.locating = status
  }

  /**
   * 处理自动获取到的位置信息
   */
  private handleAutoLocationChange(locationInfo: AutoLocationInfo) {
    // 更新位置信息
    this.temp.location = {
      longitude: locationInfo.position.longitude,
      latitude: locationInfo.position.latitude
    }

    // 更新地址信息
    this.temp.referenceAddr = locationInfo.address
    this.temp.detailAddr = locationInfo.address

    // 设置初始地图位置（以防用户稍后想手动选择）
    this.initialMapPosition = [locationInfo.position.longitude, locationInfo.position.latitude]

    // 停止自动获取
    this.autoDetectActive = false

    // 清除表单验证
    this.$nextTick(() => {
      (this.$refs.createForm as any)?.validateField('location')
    })

    this.$message.success('获取位置成功')
  }

  /**
   * 处理自动获取位置失败
   */
  private handleAutoLocationError(errorMsg: string) {
    this.$message.error('自动获取位置失败：' + errorMsg)
    this.autoDetectActive = false
  }

  /**
   * 处理位置确认事件
   */
  private handleLocationConfirm(locationInfo: LocationInfo) {
    // 更新位置信息
    this.temp.location = {
      longitude: locationInfo.position.longitude,
      latitude: locationInfo.position.latitude
    }

    // 更新地址信息
    this.temp.referenceAddr = locationInfo.address
    this.temp.detailAddr = locationInfo.address

    // 清除表单验证
    this.$nextTick(() => {
      (this.$refs.createForm as any)?.validateField('location')
    })
  }

  private handleMediaChange(media: { checkImages: string[], checkVideos: string[], referenceImages: string[] }) {
    this.temp.images = media.checkImages
    this.temp.videos = media.checkVideos
    this.temp.referenceImages = media.referenceImages
  }

  private handleUploadStatus({ uploading, progress, filesCount }: { uploading: boolean, progress: number, filesCount: number }) {
    this.isUploading = uploading
    this.uploadProgress = progress
    this.uploadingFiles = filesCount
  }

  private async submitCreate() {
    if (this.isUploading) {
      this.$message.warning('请等待文件上传完成后再提交')
      return
    }

    // 如果是自动编号模式，临时设置一个值通过验证
    const originalIndex = this.temp.index
    if (this.autoGenerateIndex) {
      this.temp.index = '自动编号' // 临时设置一个值以通过验证
    } else if (!this.temp.index && this.projectPrefix && this.customIndexNumber) {
      // 确保手动模式下index是正确的格式
      this.temp.index = `${this.projectPrefix}-${this.customIndexNumber}`
    }

    (this.$refs.createForm as any).validate(async(valid: boolean) => {
      if (valid) {
        try {
          // 如果是自动模式，则提交空字符串作为index
          const submitIndex = this.autoGenerateIndex ? '' : this.temp.index

          let params = {
            index: submitIndex || '',
            roadInfo: this.temp.roadInfo || '',
            size: this.temp.size || 0,
            material: this.temp.material || '',
            location: this.temp.location,
            referenceAddr: this.temp.referenceAddr,
            detailAddr: this.temp.detailAddr,
            checkUserId: this.temp.checkUserId as number,
            ProjectId: this.temp.ProjectId as number,
            files: [] as ILeakFile[]
          }

          if (this.temp.images.length > 0) {
            this.temp.images.forEach((image: string) => {
              params.files.push({
                filePath: image,
                fileType: FileType.CheckImage,
              })
            })
          }

          if (this.temp.videos.length > 0) {
            this.temp.videos.forEach((video: string) => {
              params.files.push({
                filePath: video,
                fileType: FileType.CheckVideo,
              })
            })
          }

          if (this.temp.referenceImages.length > 0) {
            this.temp.referenceImages.forEach((image: string) => {
              params.files.push({
                filePath: image,
                fileType: FileType.ReferenceImage,
              })
            })
          }

          await CreateLeak(params)

          // 创建成功后返回并刷新
          this.$notify({
            title: '成功',
            message: '创建漏点成功',
            type: 'success',
            duration: 2000
          })

          // 重置媒体上传组件
          const mediaUploadRef = this.$refs.mediaUpload as any;
          if (mediaUploadRef && typeof mediaUploadRef.resetMedia === 'function') {
            mediaUploadRef.resetMedia();
          }

          goBack()
        } catch (error) {
          console.error('创建漏点失败', error)
          // 恢复原始编号值
          this.temp.index = originalIndex
        }
      } else {
        // 恢复原始编号值
        this.temp.index = originalIndex
      }
    })
  }

  private handleCancel() {
    goBack()
  }

  private handleLocationDialogChange(visible: boolean) {
    this.mapDialogVisible = visible
    if (!visible) {
      // 对话框关闭时，移除可能残留的遮罩层
      setTimeout(() => {
        const modals = document.querySelectorAll('.v-modal')
        if (modals.length > 1) {
          for (let i = 1; i < modals.length; i++) {
            const modal = modals[i] as HTMLElement
            if (modal && modal.parentNode) {
              modal.parentNode.removeChild(modal)
            }
          }
        }
      }, 100)
    }
  }
}
